import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { url } from '../env';

/**
 * Hook que devuelve el status de los 4 chips del header del dashboard
 * (Contrato, Seguros, Placas, GPS) leyendo el QuoteProcess del cliente.
 *
 * - Fetch 1: `/quote-process/status/{personaId}` → trae todos los pasos y su status.
 * - Fetch 2 (condicional): si `add_insurance` está 'completed', consulta
 *   `/quote-process/{processId}/insurance` para saber si el insurance está
 *   `cancelled`. En ese caso el chip Seguros se pinta de rojo aunque el
 *   step esté técnicamente completed.
 *
 * Devuelve un map con 4 keys (`contrato`, `seguros`, `placas`, `gps`),
 * cada una con `{ status, label, raw }`:
 *   - status:  'pending' | 'current' | 'completed' | 'error' | 'warning'
 *              (lo que la UI dibuja)
 *   - label:   string corto para tooltip / texto secundario
 *   - raw:     status crudo del backend (por si se quiere usar)
 *
 * Llama `refresh()` para re-fetchear (útil después de completar un paso).
 */
const STATUS_TO_VISUAL = {
  completed:    { ui: 'completed', label: 'Completado' },
  'in-progress':{ ui: 'current',   label: 'En progreso' },
  pending:      { ui: 'pending',   label: 'Pendiente' },
  error:        { ui: 'error',     label: 'Con error' },
  unsuccessful: { ui: 'error',     label: 'No exitoso' },
  scheduled:    { ui: 'warning',   label: 'Programado' },
  postponed:    { ui: 'warning',   label: 'Aplazado' },
  paused:       { ui: 'warning',   label: 'En pausa' },
  requote:      { ui: 'warning',   label: 'Re-cotizar' },
};

const STEP_TYPE_TO_KEY = {
  contract:        'contrato',
  add_insurance:   'seguros',
  register_plates: 'placas',
  install_gps:     'gps',
};

const DEFAULT_STATE = {
  contrato: { status: 'pending', label: 'Pendiente', raw: null },
  seguros:  { status: 'pending', label: 'Pendiente', raw: null },
  placas:   { status: 'pending', label: 'Pendiente', raw: null },
  gps:      { status: 'pending', label: 'Pendiente', raw: null },
};

function mapStatus(rawStatus) {
  if (!rawStatus) return { ui: 'pending', label: 'Pendiente' };
  return STATUS_TO_VISUAL[rawStatus] || { ui: 'pending', label: rawStatus };
}

export default function useHeaderStepsStatus(personaId) {
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(false);
  const [processId, setProcessId] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    Accept: 'application/json',
  });

  const fetchStatus = useCallback(async () => {
    if (!personaId) {
      setState(DEFAULT_STATE);
      return;
    }
    setLoading(true);

    try {
      const { data } = await axios.get(
        `${url}/quote-process/status/${personaId}`,
        { headers: authHeaders() }
      );

      if (!data?.success) {
        setState(DEFAULT_STATE);
        return;
      }

      const processData = data.data || {};
      const steps = processData.steps || [];
      const pid = processData.process_id || null;
      setProcessId(pid);

      // Construye el map base desde los step_types conocidos
      const next = { ...DEFAULT_STATE };
      steps.forEach((step) => {
        const key = STEP_TYPE_TO_KEY[step.step_type];
        if (!key) return;
        const mapped = mapStatus(step.status);
        next[key] = {
          status: mapped.ui,
          label: mapped.label,
          raw: step.status,
        };
      });

      // Override Seguros si el insurance asociado está cancelled: aunque el
      // step esté completed, el chip debe verse como error (rojo) para que
      // el admin sepa que hay que reactivar.
      if (next.seguros.status === 'completed' && pid) {
        try {
          const insRes = await axios.get(
            `${url}/quote-process/${pid}/insurance`,
            { headers: authHeaders() }
          );
          const insurance = insRes?.data?.insurance;
          if (insurance && (insurance.status === 'cancelled' || insurance.effective_status === 'cancelled')) {
            next.seguros = {
              status: 'error',
              label: 'Seguro cancelado',
              raw: 'cancelled',
            };
          }
        } catch (e) {
          // Silencioso: si falla, dejamos el status del step tal cual.
        }
      }

      setState(next);
    } catch (e) {
      setState(DEFAULT_STATE);
      setProcessId(null);
    } finally {
      setLoading(false);
    }
  }, [personaId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    steps: state,
    loading,
    processId,
    refresh: fetchStatus,
  };
}
